/*
 * Copyright (c) 2020 Via Transportation, Inc. All rights reserved.
 *
 * This software is distributed under license and may not be used, copied,
 * modified, or distributed without the express written permission of
 * Via Transportation, Inc.
 */
import {
    AnyAction,
    combineReducers,
    configureStore,
    isRejectedWithValue,
    Middleware,
} from "@reduxjs/toolkit";
import { Dispatch, MiddlewareAPI, PreloadedState } from "redux";

import { api } from "store/api/shiftsApi";
import { authenticationApi } from "store/api/authenticationApiSlice";
import { configurationApi, configurationSlice } from "store/slices/configuration";
import { ratesApi } from "store/api/ratesApiSlice";
import { absencesApiSlice } from "store/api/absencesApiSlice";
import authentication from "store/slices/authentication";
import claimShifts from "store/slices/claimShifts";
import schedule from "store/slices/schedule";
import notification, { enqueueNotification } from "store/slices/notification";
import { ExtendedFetchBaseQueryError } from "common/types/ExtendedFetchBaseQueryError";
import { NotificationType } from "common/types/Notification";
import { getErrorName } from "common/util";
import { removeCookies } from "pages/LoginWeb/utils";
import shiftClaimsPolling from "store/slices/shiftClaimsPolling/slice";
import shiftBatches from "store/slices/shiftBatches/slice";
import { resetStore } from "store/actions";

export const errorHandlingMiddleware: Middleware =
    (api: MiddlewareAPI) => (next: Dispatch<AnyAction>) => (action) => {
        const actionTypes = ["api", "notification", "contactsApi", "configurationApi", "absences"];
        const actionType = action.type.split("/")[0];

        if (!action.payload) return next(action);

        if (
            actionTypes.includes(actionType) &&
            isRejectedWithValue(action) &&
            action.payload.status !== 403
        ) {
            const extendedError = action.payload as ExtendedFetchBaseQueryError;
            api.dispatch(
                enqueueNotification({
                    error: extendedError,
                    message: getErrorName(action?.payload),
                    type: NotificationType.ERROR,
                    originalArgs: action.meta?.arg?.originalArgs?.data,
                })
            );
            if (extendedError.data?.error_code === "UNSUPPORTED_CITY") {
                removeCookies();
            }
        }

        return next(action);
    };

const appReducer = combineReducers({
    configuration: configurationSlice.reducer,
    authentication,
    notification,
    claimShifts,
    schedule,
    shiftClaimsPolling,
    shiftBatches,
    [authenticationApi.reducerPath]: authenticationApi.reducer,
    [api.reducerPath]: api.reducer,
    [configurationApi.reducerPath]: configurationApi.reducer,
    [ratesApi.reducerPath]: ratesApi.reducer,
    [absencesApiSlice.reducerPath]: absencesApiSlice.reducer,
});

const rootReducer = (state: ReturnType<typeof rootReducer>, action: AnyAction) => {
    const isLogoutFulfilled =
        action.meta?.arg?.endpointName === "logout" && action.meta?.requestStatus === "fulfilled";

    if (resetStore.match(action) || isLogoutFulfilled) {
        // `baseApiUrl` and `authConfiguration` are set once by ApplicationInit on
        // mount and are not re-derived without a full page refresh. Preserve them so
        // the app keeps rendering and login stays functional after a reset, while all
        // session state (tenantId, tokens, driver info, cached queries) is cleared.
        const resetState = appReducer(undefined, action);

        return {
            ...resetState,
            configuration: {
                ...resetState.configuration,
                baseApiUrl: state?.configuration?.baseApiUrl,
                authConfiguration: state?.configuration?.authConfiguration,
            },
        };
    }

    return appReducer(state, action);
};

export const createModuleStore = <S = RootState>(
    initialState: PreloadedState<S> = {} as PreloadedState<S>
) =>
    configureStore({
        devTools: true,
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                serializableCheck: false,
            }).concat(
                api.middleware,
                authenticationApi.middleware,
                configurationApi.middleware,
                ratesApi.middleware,
                absencesApiSlice.middleware,
                errorHandlingMiddleware
            ),
        preloadedState: initialState,
        reducer: rootReducer,
    });

export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof createModuleStore>;
export type AppDispatch = AppStore["dispatch"];
